/* eslint-disable react/prop-types */
/* eslint-disable dot-notation */
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import Button from './button';
import PermissionsEditor from './permissions-editor';
import Permission from '../models/permission';
import { GroupsInheritedPermissions } from '../models/group';

interface Props {
  forms: Permission[];
  layers: Permission[];
  projects: Permission[];
  groups: Permission[];
  selectedForms: string[];
  selectedLayers: string[];
  selectedProjects: string[];
  selectedGroups: string[];
  groupsInheritedPermissions: GroupsInheritedPermissions;
  updatePermissions: (permissions: UpdatedPermission) => void;
  disabled?: boolean;
}

export interface UpdatedPermission {
  changes: object[] | string[];
  type: string;
}

export interface SelectedPermissions {
  selectedForms: string[];
  selectedLayers: string[];
  selectedProjects: string[];
  selectedGroups: string[];
}

export const Permissions: React.FC<Props> = ({
  updatePermissions,
  forms,
  projects,
  layers,
  groups,
  selectedForms,
  selectedProjects,
  selectedLayers,
  selectedGroups,
  disabled,
  groupsInheritedPermissions,
}: Props) => {
  const [showEditor, setShowEditor] = useState({
    forms: false,
    layers: false,
    projects: false,
    groups: false,
  });

  const groupsText = 'Adding Members to a Group gives them immediate access to its Apps, Map Layers, and Projects.';
  const individualAccessText = 'Additionally, you can provide access for Apps, Map Layers, and Projects that aren\'t part of a Group.';

  const openEditor = (button: string) => setShowEditor({ ...showEditor, [button]: true });

  function handlePermissionsDone(change: UpdatedPermission) {
    setShowEditor({ ...showEditor, [change.type]: false });
    updatePermissions(change);
  }

  function handlePermissionsCancel(button: string) {
    setShowEditor({ ...showEditor, [button]: false });
  }

  function renderPermissions(button: string) {
    const {
      inheritedForms, inheritedLayers, inheritedProjects,
      inheritedFormIds, inheritedProjectIds, inheritedLayerIds,
    } = groupsInheritedPermissions;
    let buttonObj: any;

    switch (button) {
      case 'forms':
        buttonObj = {
          type: 'forms',
          selected: selectedForms.length + inheritedFormIds.length,
          total: forms.length,
          title: forms.length > 0 ? 'Select Apps' : 'No Apps',
          objects: forms,
          selectedIDs: selectedForms,
          preselectedObjects: inheritedForms,
          show: showEditor['forms'],
          testID: 'forms-btn',
        };
        break;
      case 'layers':
        buttonObj = {
          type: 'layers',
          selected: selectedLayers.length + inheritedLayerIds.length,
          total: layers.length,
          title: layers.length > 0 ? 'Select Layers' : 'No Layers',
          objects: layers,
          selectedIDs: selectedLayers,
          preselectedObjects: inheritedLayers,
          show: showEditor['layers'],
          testID: 'layers-btn',
        };
        break;
      case 'projects':
        buttonObj = {
          type: 'projects',
          selected: selectedProjects.length + inheritedProjectIds.length,
          total: projects.length,
          title: projects.length > 0 ? 'Select Projects' : 'No Projects',
          objects: projects,
          selectedIDs: selectedProjects,
          preselectedObjects: inheritedProjects,
          show: showEditor['projects'],
          testID: 'projs-btn',
        };
        break;
      case 'groups':
        buttonObj = {
          type: 'groups',
          selected: selectedGroups.length,
          total: groups.length,
          title: groups.length > 0 ? 'Select Groups' : 'No Groups',
          objects: groups,
          selectedIDs: selectedGroups,
          show: showEditor['groups'],
          testID: 'groups-btn',
        };
        break;
      default:
        return;
    }

    return (
      <>
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'start', ml: 0,
        }}
        >
          <Button
            small
            affirm
            disabled={disabled || buttonObj.title.substring(0, 2) === 'No'}
            title={buttonObj.title}
            style={{ marginRight: 10 }}
            onClick={() => openEditor(buttonObj.type)}
          >
            {buttonObj.title}
          </Button>
          {(!disabled && buttonObj.title.substring(0, 2) !== 'No') && (
            <Typography variant="body2" data-testid={`selected-${buttonObj.type}-count`}>
              {buttonObj.selected}
              {' '}
              selected
            </Typography>
          )}
        </Box>
        <PermissionsEditor
          showTop
          type={buttonObj.type}
          show={buttonObj.show}
          objects={buttonObj.objects}
          selectedIDs={buttonObj.selectedIDs}
          preselectedObjects={buttonObj.preselectedObjects}
          onDone={(changes: string[] | object[]) => handlePermissionsDone({
            changes, type: buttonObj.type,
          })}
          onCancel={() => handlePermissionsCancel(buttonObj.type)}
        />
      </>
    );
  }

  return (
    <>
      <>
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Typography variant="h6">2. Add to Groups</Typography>
        </Box>
        <Typography variant="body2">{groupsText}</Typography>
        <Box sx={{ display: 'flex', mt: 1 }}>
          <div>{renderPermissions('groups')}</div>
        </Box>
        <hr />
      </>
      <Typography variant="h6" sx={{ mb: 1 }}>
        3. Manage Individual Access
      </Typography>
      <Typography variant="body2">{individualAccessText}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
        {renderPermissions('forms')}
        {renderPermissions('layers')}
        {renderPermissions('projects')}
      </Box>
    </>
  );
};
